import { useMemo } from "react";
import constate from "constate";
import { QueryStatus } from "react-query";
import { useAccount } from "../../api/hooks";

type ExceptionInfo = {
  title?: string;
  message: string;
};
type GateStatus =
  | {
      isAccessible: true;
      status: "loading";
      error?: undefined;
      info?: undefined;
    }
  | {
      isAccessible: true;
      status: QueryStatus;
      error?: ExceptionInfo;
      info?: undefined;
    }
  | {
      isAccessible: false;
      status: QueryStatus;
      error?: undefined;
      info?: ExceptionInfo;
    };

export const _useAccountGate = ({
  accountNumber,
}: {
  accountNumber: string;
}): GateStatus => {
  if (typeof accountNumber === "undefined") {
    throw new Error("You must provide a valid account number.");
  }

  const { data: account, status, isError, isLoading } = useAccount(
    accountNumber,
  );

  const isAccessible =
    account?.childAccountNumbers.length === 0 &&
    account?.parentAccountNumber === null;

  return useMemo(() => {
    // Default represent a valid or loading state
    let result: GateStatus = {
      isAccessible: true,
      status,
    };

    if (isError) {
      result = {
        isAccessible: true,
        status,
        error: {
          title: "We could not load this account.",
          message: "Please try again later",
        },
      };
    }

    if (!isError && !isAccessible && !isLoading) {
      result = {
        isAccessible: false,
        status,
        info: {
          message:
            "This account is part of a parent / child setup.  Refer to Junifer desktop for more information.",
        },
      };
    }

    return result;
  }, [isAccessible, isError, isLoading, status]);
};

export const [AccountGateProvider, useAccountGate] = constate(_useAccountGate);
